import { supabase } from '../lib/supabase'
import { StudyPageContent } from '../types/database'
import { getCachedOrFetch, cacheData, invalidateCache } from '../utils/cacheUtils'

export const studyPageContentService = {
  // Get study page content for a specific module
  async getStudyPageContent(moduleId: string): Promise<StudyPageContent | null> {
    // Use cache with module id as key
    const cacheKey = `study_page_content_${moduleId}`;
    
    return await getCachedOrFetch(
      cacheKey,
      async () => {
        const { data, error } = await supabase
          .from('study_page_content')
          .select('*')
          .eq('module_id', moduleId)
          .single();

        // Handle the "not found" case gracefully
        if (error && error.code === 'PGRST116') {
          return null;
        }
        
        // Re-throw any other errors
        if (error) {
          console.error('Error fetching study page content:', error);
          throw error;
        }

        return data;
      },
      15 * 60 * 1000 // 15 minutes cache TTL
    );
  },

  // Create or update study page content using upsert
  async saveStudyPageContent(moduleId: string, content: any): Promise<StudyPageContent> {
    try {
      // Map the content object to individual columns with proper JSONB defaults
      const payload = {
        module_id: moduleId,
        title: content.title || '',
        summary: content.summary || {},
        diagrams: content.diagrams || [],
        flashcards: content.flashcards || [],
        quiz: content.quiz || {},
        audio: content.audio || {}
      };

      // Use upsert to handle both insert and update cases
      const { data, error } = await supabase
        .from('study_page_content')
        .upsert(
          payload,
          { 
            onConflict: 'module_id' 
          }
        )
        .select()
        .single();

      if (error) {
        console.error('Error saving study page content:', error);
        throw error;
      }

      // Update the cache with the new content
      if (data) {
        cacheData(`study_page_content_${moduleId}`, data);
      }

      return data;
    } catch (error) {
      console.error('Error in saveStudyPageContent:', error);
      throw error;
    }
  },
  
  // Prefetch study page content for multiple modules
  async prefetchStudyPageContents(moduleIds: string[]): Promise<void> {
    const promises = moduleIds.map(id => this.getStudyPageContent(id));
    await Promise.allSettled(promises);
  }
};